/* eslint-disable no-nested-ternary, no-console */
import { useEffect, useMemo, useCallback, useState, useContext } from 'react';
import { useSelector } from 'react-redux';
import { useGetClaimsMutation, useLoadMoreClaimsMutation, useGetSavedClaimsQuery } from '../../data/claim';
import { useGetPetInfoQuery } from '../../data/policy';
import { regroupRecentNotAdmittedClaims } from '../../helpers/claimHelpers';
import { sortClaims } from '../../utils/sort';
import { SLICES } from '../../constants/slices';
import { FeatureFlagContext } from '../../contextProviders/featureFlagContext';

export const NUMBER_OF_INIT_CLAIMS_DISPLAY = 5;
export const PAGE_SIZE = 10;

export const PANEL_FILTERS = {
  ALL: 'All claims',
  OPEN: 'My open claims',
  COMPLETED: 'My completed claims',
  SAVED: 'My saved claims'
};

export const CLAIM_TYPES = {
  OPEN: 'open',
  COMPLETED: 'completed'
};

const extractClaimsArray = (payload, isOpen) =>
  Array.isArray(payload)
    ? payload
    : isOpen
    ? payload?.OpenClaims ?? payload?.Claims ?? payload?.Items ?? []
    : payload?.CompletedClaims ?? payload?.Claims ?? payload?.Items ?? [];

export const useClaimsLogic = (locationSearch = '') => {
  const { cspConfiguration } = useContext(FeatureFlagContext);
  const isV3PenTestRemediationEndpointsEnabled = cspConfiguration?.IsV3PenTestRemediationEndpointsEnabled;
  const { authDetails } = useSelector((state) => state?.[SLICES.AUTHENTICATION]);
  const fullPolicyNumber = useSelector((state) => state?.[SLICES.PET_DETAILS]?.fullPolicyNumber);
  const { auth0AccessToken } = authDetails;
  const { data: petDetails = [], isLoading: petLoading } = useGetPetInfoQuery(auth0AccessToken, {
    skip: !auth0AccessToken
  });
  const { contactId, partnerNumber } = authDetails;
  const [fetchInitialClaims, { isLoading: initialClaimsLoading }] = useGetClaimsMutation();
  const [loadMoreClaims, { isLoading: loadMoreLoading }] = useLoadMoreClaimsMutation();

  const { data: savedClaims = [], isLoading: savedLoading } = useGetSavedClaimsQuery(
    {
      contactId,
      partnerNumber,
      auth0Token: auth0AccessToken,
      isV3ApiEnabled: isV3PenTestRemediationEndpointsEnabled
    },
    {
      skip:
        petLoading ||
        !contactId ||
        !partnerNumber ||
        isV3PenTestRemediationEndpointsEnabled === undefined ||
        (isV3PenTestRemediationEndpointsEnabled && !auth0AccessToken)
    }
  );

  const policiesPayload = useMemo(
    () =>
      petDetails.map((p) => ({
        PolicyNumber: p.PolicyNumber,
        PetName: p.Name,
        Status: p.PetProfile?.Status ?? p.PolicyDetails.Status,
        PetProfilePicture: p.PetProfilePicture
      })),
    [petDetails]
  );

  const [openClaims, setOpenClaims] = useState([]);
  const [completedClaims, setCompletedClaims] = useState([]);

  const [openStart, setOpenStart] = useState(NUMBER_OF_INIT_CLAIMS_DISPLAY);
  const [completedStart, setCompletedStart] = useState(NUMBER_OF_INIT_CLAIMS_DISPLAY);

  const [openHasMore, setOpenHasMore] = useState(false);
  const [completedHasMore, setCompletedHasMore] = useState(false);

  const [savedSliceEnd, setSavedSliceEnd] = useState(NUMBER_OF_INIT_CLAIMS_DISPLAY);
  const savedVisible = useMemo(
    () => (savedClaims?.length ? sortClaims(savedClaims).slice(0, savedSliceEnd) : []),
    [savedClaims, savedSliceEnd]
  );
  const savedHasMore = savedSliceEnd < savedClaims?.length;
  const handleSavedShowMore = () => setSavedSliceEnd((prev) => Math.min(prev + PAGE_SIZE, savedClaims.length));

  const [policyFilter, setPolicyFilter] = useState(fullPolicyNumber);
  const [panelFilter, setPanelFilter] = useState(PANEL_FILTERS.ALL);

  useEffect(() => {
    if (!petDetails.length || !auth0AccessToken) return;

    fetchInitialClaims({ data: policiesPayload, auth0Token: auth0AccessToken })
      .unwrap()
      .then((res) => {
        const openArr = res.OpenClaims ?? [];
        const compArr = res.CompletedClaims ?? [];
        const regroupedClaims = regroupRecentNotAdmittedClaims(openArr, compArr);

        setOpenClaims(regroupedClaims.openClaims);
        setCompletedClaims(regroupedClaims.completedClaims);

        setOpenHasMore(openArr.length >= NUMBER_OF_INIT_CLAIMS_DISPLAY);
        setCompletedHasMore(compArr.length >= NUMBER_OF_INIT_CLAIMS_DISPLAY);
      })
      .catch((e) => console.error('initial claims fetch failed', e));
  }, [petDetails, policiesPayload, fetchInitialClaims, auth0AccessToken]);

  const handleShowMore = useCallback(
    async (claimType) => {
      const isOpen = claimType === CLAIM_TYPES.OPEN;
      const body = {
        Policies: policiesPayload,
        StartPosition: isOpen ? openStart : completedStart,
        Take: PAGE_SIZE,
        IsOpenClaimsRequest: isOpen,
        IsCompletedClaimsRequest: !isOpen,
        PetName: 'All pets'
      };

      try {
        const resp = await loadMoreClaims({ body, auth0Token: auth0AccessToken }).unwrap();
        const append = extractClaimsArray(resp, isOpen);

        if (isOpen) {
          setOpenClaims((prev) => [...prev, ...append]);
          setOpenStart((prev) => prev + PAGE_SIZE);
          setOpenHasMore(append.length === PAGE_SIZE);
        } else {
          const regroupedClaims = regroupRecentNotAdmittedClaims([], append);

          setOpenClaims((prev) => [...prev, ...regroupedClaims.openClaims]);
          setCompletedClaims((prev) => [...prev, ...regroupedClaims.completedClaims]);
          setCompletedStart((prev) => prev + PAGE_SIZE);
          setCompletedHasMore(append.length === PAGE_SIZE);
        }
      } catch (err) {
        console.error('loadMore failed', err);
      }
    },
    [policiesPayload, openStart, completedStart, loadMoreClaims, auth0AccessToken]
  );

  return {
    petLoading,
    initialClaimsLoading,
    savedLoading,
    loadMoreLoading,

    petDetails,
    savedVisible,
    openClaims,
    completedClaims,

    savedHasMore,
    openHasMore,
    completedHasMore,

    panelFilter,
    setPanelFilter,
    policyFilter,
    setPolicyFilter,

    handleSavedShowMore,
    handleShowMore,

    PANEL_FILTERS,
    CLAIM_TYPES
  };
};
